import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Dropdown, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { Flex } from '~/styles';

type EntityType = '' | 'PERSON' | 'BUSINESS';
type VerificationStatusFilter = '' | 'UNVERIFIED' | 'PENDING' | 'MANUAL_REVIEW' | 'VERIFIED' | 'DENIED';

const TYPE_OPTIONS: { label: string; value: EntityType }[] = [
  { label: 'Any', value: '' },
  { label: 'Person', value: 'PERSON' },
  { label: 'Business', value: 'BUSINESS' },
];

const VERIFICATION_STATUS_OPTIONS: { label: string; value: VerificationStatusFilter }[] = [
  { label: 'Any', value: '' },
  { label: 'Unverified', value: 'UNVERIFIED' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Manual review', value: 'MANUAL_REVIEW' },
  { label: 'Verified', value: 'VERIFIED' },
  { label: 'Denied', value: 'DENIED' },
];

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 var(--page-margin) 24px;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    padding-top: 8px;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

interface FormValues {
  type: EntityType;
  verificationStatus: VerificationStatusFilter;
  query: string;
}

interface EntityFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  showSearch: boolean;
  initialType: EntityType;
  initialVerificationStatus: VerificationStatusFilter;
  initialQuery: string;
  onApply: (values: { type: EntityType; verificationStatus: VerificationStatusFilter; query: string }) => void;
}

export const EntityFiltersModal: React.FC<EntityFiltersModalProps> = ({
  isOpen,
  onClose,
  showSearch,
  initialType,
  initialVerificationStatus,
  initialQuery,
  onApply,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      type: initialType,
      verificationStatus: initialVerificationStatus,
      query: initialQuery,
    },
  });
  const { watch, setValue, reset, getValues } = methods;

  useEffect(() => {
    if (!isOpen) return;
    reset({
      type: initialType,
      verificationStatus: initialVerificationStatus,
      query: initialQuery,
    });
  }, [isOpen, initialType, initialVerificationStatus, initialQuery, reset]);

  const handleApply = () => {
    const fv = getValues();
    onApply({
      type: fv.type,
      verificationStatus: fv.verificationStatus,
      query: fv.query,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Medium}>
      <Modal.Header title="Filters" />
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={16}>
          <Section>
            <Label>Type</Label>
            <Dropdown
              size="small"
              fullWidth
              active={watch('type')}
              options={TYPE_OPTIONS}
              label={TYPE_OPTIONS.find((o) => o.value === watch('type'))?.label ?? 'Any'}
              onChange={(value: EntityType) => setValue('type', value)}
            />
          </Section>

          <Section>
            <Label>Status</Label>
            <Dropdown
              size="small"
              fullWidth
              active={watch('verificationStatus')}
              options={VERIFICATION_STATUS_OPTIONS}
              label={VERIFICATION_STATUS_OPTIONS.find((o) => o.value === watch('verificationStatus'))?.label ?? 'Any'}
              onChange={(value: VerificationStatusFilter) => setValue('verificationStatus', value)}
            />
          </Section>

          {showSearch && (
            <Section>
              <Label>Search</Label>
              <Input
                size="small"
                value={watch('query')}
                placeholder="Search entities..."
                onChange={(value: string) => setValue('query', value)}
              />
            </Section>
          )}

          <Modal.Footer>
            <Button size="small" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button size="small" onClick={handleApply}>
              Apply
            </Button>
          </Modal.Footer>
        </Flex>
      </FormProvider>
    </Modal>
  );
};
